import { uniformType, drawMode, bufferType } from "_src/engine/constants";
import context from "_context";

let _gl = null;

export default {

  getContext(canvas, params = { alpha     : false,
                                antialias : false,
                                stencil   : true }) {

    if (_gl) {
      return _gl;
    }

    if (!canvas) {
      return null;
    }

    let gl = context.getContext(canvas, params);

    _gl = gl;

    return gl;

  },

  loseContext() {
    _gl.getExtension("WEBGL_lose_context").loseContext();
  },

  checkWebGL2() {
    return context.checkWebGL2();
  },

  setUniform1f(loc, value) {
    _gl.uniform1f(loc, value);
  },

  setUniform1fv(loc, value) {
    _gl.uniform1fv(loc, value);
  },

  setUniform1i(loc, value) {
    _gl.uniform1i(loc, value);
  },

  setUniform1iv(loc, value) {
    _gl.uniform1iv(loc, value);
  },

  setUniform2f(loc, value) {
    _gl.uniform2f(loc, value[0], value[1]);
  },

  setUniform2fv(loc, value) {
    _gl.uniform2fv(loc, value);
  },

  setUniform2i(loc, value) {
    _gl.uniform2i(loc, value[0], value[1]);
  },

  setUniform2iv(loc, value) {
    _gl.uniform2iv(loc, value);
  },

  setUniform3f(loc, value) {
    _gl.uniform3f(loc, value[0], value[1], value[2]);
  },

  setUniform3fv(loc, value) {
    _gl.uniform3fv(loc, value);
  },

  setUniform3i(loc, value) {
    _gl.uniform3i(loc, value[0], value[1], value[2]);
  },

  setUniform3iv(loc, value) {
    _gl.uniform3iv(loc, value);
  },

  setUniform4f(loc, value) {
    _gl.uniform4f(loc, value[0], value[1], value[2], value[3]);
  },

  setUniform4fv(loc, value) {
    _gl.uniform4fv(loc, value);
  },

  setUniform4i(loc, value) {
    _gl.uniform4i(loc, value[0], value[1], value[2], value[3]);
  },

  setUniform4iv(loc, value) {
    _gl.uniform4iv(loc, value);
  },

  setUniformMatrix2fv(loc, value) {
    _gl.uniformMatrix2fv(loc, false, value);
  },

  setUniformMatrix3fv(loc, value) {
    _gl.uniformMatrix3fv(loc, false, value);
  },

  setUniformMatrix4fv(loc, value) {
    _gl.uniformMatrix4fv(loc, false, value);
  },

  getUniformSetter(type) {
    switch (type) {

      case uniformType.FLOAT1:
        return this.setUniform1f;
      case uniformType.FLOAT1V:
        return this.setUniform1fv;
      case uniformType.FLOAT2:
        return this.setUniform2f;
      case uniformType.FLOAT2V:
        return this.setUniform2fv;
      case uniformType.FLOAT3:
        return this.setUniform3f;
      case uniformType.FLOAT3V:
        return this.setUniform3fv;
      case uniformType.FLOAT4:
        return this.setUniform4f;
      case uniformType.FLOAT4V:
        return this.setUniform4fv;

      case uniformType.INT1:
        return this.setUniform1i;
      case uniformType.INT1V:
        return this.setUniform1iv;
      case uniformType.INT2:
        return this.setUniform2i;
      case uniformType.INT2V:
        return this.setUniform2iv;
      case uniformType.INT3:
        return this.setUniform3i;
      case uniformType.INT3V:
        return this.setUniform3iv;
      case uniformType.INT4:
        return this.setUniform4i;
      case uniformType.INT4V:
        return this.setUniform4iv;

      case uniformType.MAT2:
        return this.setUniformMatrix2fv;
      case uniformType.MAT3:
        return this.setUniformMatrix3fv;
      case uniformType.MAT4:
        return this.setUniformMatrix4fv;

    }
  },

  getGLDrawMode(mode) {

    if (!_gl) {
      return null;
    }

    switch(mode) {
      case drawMode.LINES:
        return _gl.LINES;
      case drawMode.TRIANGLE_STRIP:
        return _gl.TRIANGLE_STRIP;
      case drawMode.TRIANGLE_FAN:
        return _gl.TRIANGLE_FAN;
      case drawMode.POINTS:
        return _gl.POINTS;
      default:
        return _gl.TRIANGLES;
    }
  },

  getGLBufferType(type) {

    if (!_gl) {
      return null;
    }

    switch (type) {
      case bufferType.F32_1:
      case bufferType.F32_2:
      case bufferType.F32_3:
      case bufferType.F32_4:
        return _gl.FLOAT;
      case bufferType.UI8_1:
      case bufferType.UI8_2:
      case bufferType.UI8_3:
      case bufferType.UI8_4:
        return _gl.UNSIGNED_BYTE;
      case bufferType.UI16_1:
      case bufferType.UI16_2:
      case bufferType.UI16_3:
        return _gl.UNSIGNED_SHORT;
      case bufferType.I8_1:
      case bufferType.I8_2:
      case bufferType.I8_3:
        return _gl.BYTE;
      case bufferType.I16_1:
      case bufferType.I16_2:
      case bufferType.I16_3:
        return _gl.SHORT;
      case bufferType.UI32_1:
      case bufferType.UI32_2:
      case bufferType.UI32_3:
      case bufferType.I32_1:
      case bufferType.I32_2:
      case bufferType.I32_3:
        // Such attributes are not allowed in webgl
        // They are used for consistency
        return null;
      default:
        throw "Unsupported buffer type.";
    }
  },

  setupTextures(textures) {

    for (let i = 0; i < textures.length; ++i) {

      _gl.activeTexture(_gl.TEXTURE0 + i);
      _gl.bindTexture(textures[i].target, textures[i].pixelBuffer);

    }

  },

  readPixelsSync(framebuffer, x, y, width, height, storage) {

    _gl.bindFramebuffer(_gl.FRAMEBUFFER, framebuffer.fbo);
    // Only two format/type parameter pairs are accepted. GL_RGBA/GL_UNSIGNED_BYTE is always
    // accepted on default readbuffer. The other acceptable pair can be discovered by querying
    // GL_IMPLEMENTATION_COLOR_READ_FORMAT and GL_IMPLEMENTATION_COLOR_READ_TYPE.
    // _gl.getParameter(_gl.IMPLEMENTATION_COLOR_READ_FORMAT)
    // _gl.getParameter(_gl.IMPLEMENTATION_COLOR_READ_TYPE)
    _gl.readPixels(x, y, width, height, _gl.RGBA, _gl.UNSIGNED_BYTE, storage);
    _gl.bindFramebuffer(_gl.FRAMEBUFFER, null);

    return storage;

  },

  async readPixelsAsync(framebuffer, x, y, width, height, storage) {

    const buf = _gl.createBuffer();
    _gl.bindFramebuffer(_gl.FRAMEBUFFER, framebuffer.fbo);
    _gl.bindBuffer(_gl.PIXEL_PACK_BUFFER, buf);
    _gl.bufferData(_gl.PIXEL_PACK_BUFFER, storage.byteLength, _gl.STREAM_READ);
    _gl.readPixels(x, y, width, height, _gl.RGBA, _gl.UNSIGNED_BYTE, 0);
    _gl.bindBuffer(_gl.PIXEL_PACK_BUFFER, null);
    _gl.bindFramebuffer(_gl.FRAMEBUFFER, null);

    await getBufferSubDataAsync(_gl, _gl.PIXEL_PACK_BUFFER, buf, 0, storage);

    _gl.deleteBuffer(buf);
    return storage;
  },

  getMaxRenderBufferSize() {
    return _gl ? _gl.getParameter(_gl.MAX_RENDERBUFFER_SIZE): 0;
  },

  cleanup() {
    _gl = null;
  }

}

function clientWaitAsync(gl, sync, interval_ms) {
  return new Promise((resolve) => {
    function test() {
      const status = gl.getSyncParameter(sync, gl.SYNC_STATUS);

      if (status === gl.SIGNALED) {
        resolve();
      } else {
        setTimeout(test, interval_ms);
      }
    }
    test();
  });
}

async function getBufferSubDataAsync(gl, target, buffer, srcByteOffset, dstBuffer,
    /* optional */ dstOffset, /* optional */ length) {
  const sync = gl.fenceSync(gl.SYNC_GPU_COMMANDS_COMPLETE, 0);
  gl.flush();

  await clientWaitAsync(gl, sync, 10);
  gl.deleteSync(sync);

  gl.bindBuffer(target, buffer);
  gl.getBufferSubData(target, srcByteOffset, dstBuffer, dstOffset, length);
  gl.bindBuffer(target, null);

  return dstBuffer;
}
